import { FormEvent, useCallback, useEffect, useState } from 'react';
import {
  mailApi,
  type Campaign,
  type CampaignDetail,
  type CampaignStats,
  type EnrollFilter,
  type Enrollment,
  type MailAccount,
  type Preset,
  type StepInput,
} from '../lib/mailApi';

const errMsg = (e: unknown) => (e instanceof Error ? e.message : 'Something went wrong');

const STATUS_STYLE: Record<string, string> = {
  draft: 'bg-slate-100 text-slate-600',
  active: 'bg-green-100 text-green-700',
  paused: 'bg-amber-100 text-amber-800',
  completed: 'bg-slate-100 text-slate-500',
  replied: 'bg-green-100 text-green-700',
  bounced: 'bg-red-100 text-red-700',
  unsubscribed: 'bg-red-50 text-red-600',
  stopped: 'bg-slate-100 text-slate-500',
};

const Badge = ({ value }: { value: string }) => (
  <span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${STATUS_STYLE[value] ?? 'bg-slate-100'}`}>{value}</span>
);

function Funnel({ s }: { s: CampaignStats }) {
  const tiles = [
    { label: 'Leads enrolled', value: s.enrolled },
    { label: 'Emails sent', value: s.emailsSent },
    { label: 'Leads contacted', value: s.leadsContacted },
    { label: 'Opened', value: `${s.leadsOpened}`, sub: `${s.openRate}% · ${s.opens} opens` },
    { label: 'Clicked', value: s.leadsClicked },
    { label: 'Replied', value: s.replied, sub: `${s.replyRate}%`, good: true },
    { label: 'Bounced', value: s.bounced, bad: s.bounced > 0 },
    { label: 'Unsubscribed', value: s.unsubscribed },
  ];
  return (
    <div className="grid grid-cols-2 gap-3 md:grid-cols-4 xl:grid-cols-8">
      {tiles.map((t) => (
        <div key={t.label} className="rounded-lg border border-line bg-white p-3">
          <div className={`text-xl font-extrabold ${t.good ? 'text-good' : t.bad ? 'text-poor' : 'text-ink'}`}>{t.value}</div>
          <div className="text-[11px] uppercase tracking-wide text-slate-400">{t.label}</div>
          {t.sub && <div className="text-xs text-slate-500">{t.sub}</div>}
        </div>
      ))}
    </div>
  );
}

/* ─────────────────────────────── list ─────────────────────────────── */

function NewCampaign({ presets, accounts, onCreated }: { presets: Preset[]; accounts: MailAccount[]; onCreated: (id: string) => void }) {
  const [name, setName] = useState('');
  const [service, setService] = useState(presets[0]?.key ?? 'website_revamp');
  const [mailAccountId, setMailAccountId] = useState(accounts[0]?.id ?? '');
  const [error, setError] = useState<string | null>(null);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    try {
      const c = await mailApi.createCampaign({ name, service, mailAccountId });
      onCreated(c.id);
    } catch (err) {
      setError(errMsg(err));
    }
  }

  if (!accounts.length) {
    return <div className="card text-sm text-slate-500">Connect a mailbox in <b>Settings</b> first.</div>;
  }
  return (
    <form onSubmit={submit} className="card space-y-4">
      <h3 className="font-bold text-ink">New campaign</h3>
      <div className="grid gap-4 md:grid-cols-3">
        <div>
          <label className="label">Name</label>
          <input className="input" required value={name} onChange={(e) => setName(e.target.value)} placeholder="Dentists London: website revamp" />
        </div>
        <div>
          <label className="label">Template</label>
          <select className="input" value={service} onChange={(e) => setService(e.target.value)}>
            {presets.map((p) => <option key={p.key} value={p.key}>{p.label} ({p.steps.length} emails)</option>)}
          </select>
          <p className="mt-1 text-xs text-slate-400">{presets.find((p) => p.key === service)?.description}</p>
        </div>
        <div>
          <label className="label">Send from</label>
          <select className="input" value={mailAccountId} onChange={(e) => setMailAccountId(e.target.value)}>
            {accounts.map((a) => <option key={a.id} value={a.id}>{a.fromEmail}</option>)}
          </select>
        </div>
      </div>
      {error && <p className="text-sm text-poor">{error}</p>}
      <button className="btn-primary">Create</button>
    </form>
  );
}

/* ───────────────────────────── steps editor ─────────────────────────── */

function StepsEditor({ campaign, variables, onSaved }: { campaign: CampaignDetail; variables: string[]; onSaved: () => void }) {
  const [steps, setSteps] = useState<StepInput[]>(campaign.steps.map(({ delayDays, subject, body }) => ({ delayDays, subject, body })));
  const [dirty, setDirty] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const [preview, setPreview] = useState<{ step: number; subject: string; body: string; who: string } | null>(null);

  const update = (i: number, patch: Partial<StepInput>) => {
    setSteps((s) => s.map((x, j) => (j === i ? { ...x, ...patch } : x)));
    setDirty(true);
  };

  async function save() {
    setMsg(null);
    try {
      await mailApi.saveSteps(campaign.id, steps);
      setDirty(false);
      setMsg('Saved.');
      onSaved();
    } catch (err) {
      setMsg(errMsg(err));
    }
  }

  async function showPreview(i: number) {
    if (dirty) await save();
    try {
      const p = await mailApi.preview(campaign.id, i);
      setPreview({ step: i, subject: p.subject, body: p.body, who: `${p.lead.businessName} <${p.lead.email ?? 'no email'}>` });
    } catch (err) {
      setMsg(errMsg(err));
    }
  }

  let day = 0;
  return (
    <div className="space-y-4">
      <p className="text-sm text-slate-500">
        Variables: {variables.map((v) => <code key={v} className="mr-1 rounded bg-canvas px-1 text-xs">{`{{${v}}}`}</code>)} · Fallback: <code className="rounded bg-canvas px-1 text-xs">{'{{firstName|there}}'}</code>.
        Signature, postal address and unsubscribe line are added automatically. A follow-up with an empty subject is sent as a reply in the same thread.
      </p>
      {steps.map((s, i) => {
        day += i === 0 ? 0 : s.delayDays;
        return (
          <div key={i} className="card space-y-3">
            <div className="flex flex-wrap items-center gap-3">
              <span className="font-bold text-ink">Email {i + 1}</span>
              <span className="text-xs text-slate-400">day {day}</span>
              {i > 0 && (
                <label className="flex items-center gap-2 text-sm text-slate-500">
                  wait
                  <input className="input !w-20 !py-1" type="number" min={1} max={60} value={s.delayDays} onChange={(e) => update(i, { delayDays: Number(e.target.value) })} />
                  days after previous
                </label>
              )}
              <div className="ml-auto flex gap-2">
                <button className="btn-ghost !py-1 text-xs" onClick={() => void showPreview(i)}>Preview</button>
                {steps.length > 1 && (
                  <button className="btn-ghost !py-1 text-xs" onClick={() => { setSteps((x) => x.filter((_, j) => j !== i)); setDirty(true); }}>Remove</button>
                )}
              </div>
            </div>
            <input className="input" placeholder={i === 0 ? 'Subject' : 'Subject (empty = reply in same thread)'} value={s.subject} onChange={(e) => update(i, { subject: e.target.value })} />
            <textarea className="input min-h-[180px] font-mono text-[13px]" value={s.body} onChange={(e) => update(i, { body: e.target.value })} />
          </div>
        );
      })}
      <div className="flex flex-wrap items-center gap-3">
        {steps.length < 8 && (
          <button className="btn-ghost" onClick={() => { setSteps((x) => [...x, { delayDays: 4, subject: '', body: 'Hi {{firstName|there}},\n\n' }]); setDirty(true); }}>
            + Add follow-up
          </button>
        )}
        <button className="btn-primary" disabled={!dirty} onClick={() => void save()}>Save emails</button>
        {msg && <span className="text-sm text-slate-500">{msg}</span>}
      </div>

      {preview && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={() => setPreview(null)}>
          <div className="card max-h-[85vh] w-full max-w-2xl overflow-auto" onClick={(e) => e.stopPropagation()}>
            <div className="mb-1 text-xs text-slate-400">Email {preview.step + 1} for {preview.who}</div>
            <div className="mb-3 font-bold text-ink">{preview.subject}</div>
            <pre className="whitespace-pre-wrap font-sans text-sm text-ink">{preview.body}</pre>
            <p className="mt-4 text-xs text-slate-400">+ signature, postal address and unsubscribe link</p>
            <button className="btn-ghost mt-4" onClick={() => setPreview(null)}>Close</button>
          </div>
        </div>
      )}
    </div>
  );
}

/* ─────────────────────────────── leads ─────────────────────────────── */

function stepState(e: Enrollment) {
  const sent = e.outreach.length;
  const opened = e.outreach.some((o) => o.openCount > 0);
  const clicked = e.outreach.some((o) => o.clickCount > 0);
  return { sent, opened, clicked };
}

function LeadsTab({ campaign, onChanged }: { campaign: CampaignDetail; onChanged: () => void }) {
  const [filter, setFilter] = useState<EnrollFilter>({ minScore: 60, onlyNew: true, limit: 50 });
  const [hasWebsite, setHasWebsite] = useState<'any' | 'yes' | 'no'>(campaign.service === 'new_website' ? 'no' : campaign.service === 'website_revamp' ? 'yes' : 'any');
  const [msg, setMsg] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function enroll(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setMsg(null);
    try {
      const r = await mailApi.enroll(campaign.id, {
        filter: { ...filter, hasWebsite: hasWebsite === 'any' ? undefined : hasWebsite === 'yes' },
      });
      const skipped = Object.entries(r.skipped).map(([k, v]) => `${v} ${k}`).join(', ');
      setMsg(`Added ${r.added} leads.${skipped ? ` Skipped: ${skipped}.` : ''}${r.added === 0 && !skipped ? ' No leads matched. Lower the minimum score, or find and enrich more leads first.' : ''}`);
      onChanged();
    } catch (err) {
      setMsg(errMsg(err));
    } finally {
      setBusy(false);
    }
  }

  const set = <K extends keyof EnrollFilter>(k: K, v: EnrollFilter[K]) => setFilter((f) => ({ ...f, [k]: v }));

  return (
    <div className="space-y-4">
      <form onSubmit={enroll} className="card space-y-3">
        <div>
          <h3 className="font-bold text-ink">Add leads</h3>
          <p className="text-sm text-slate-500">Only leads with an email, not on the do-not-contact list and not active in another campaign are added. Highest score first.</p>
        </div>
        <div className="grid gap-3 md:grid-cols-6">
          <div><label className="label">Min score</label><input className="input" type="number" min={0} max={100} value={filter.minScore ?? 0} onChange={(e) => set('minScore', Number(e.target.value))} /></div>
          <div><label className="label">City</label><input className="input" value={filter.city ?? ''} onChange={(e) => set('city', e.target.value || undefined)} /></div>
          <div><label className="label">Country</label><input className="input" value={filter.country ?? ''} onChange={(e) => set('country', e.target.value || undefined)} /></div>
          <div><label className="label">Category</label><input className="input" placeholder="dentist" value={filter.category ?? ''} onChange={(e) => set('category', e.target.value || undefined)} /></div>
          <div>
            <label className="label">Website</label>
            <select className="input" value={hasWebsite} onChange={(e) => setHasWebsite(e.target.value as 'any' | 'yes' | 'no')}>
              <option value="any">Any</option><option value="yes">Has website</option><option value="no">No website</option>
            </select>
          </div>
          <div><label className="label">How many</label><input className="input" type="number" min={1} max={500} value={filter.limit ?? 50} onChange={(e) => set('limit', Number(e.target.value))} /></div>
        </div>
        <label className="flex items-center gap-2 text-sm text-slate-600">
          <input type="checkbox" checked={filter.onlyNew ?? true} onChange={(e) => set('onlyNew', e.target.checked)} /> Only leads still in “New” (never contacted)
        </label>
        <div className="flex items-center gap-3">
          <button className="btn-primary" disabled={busy}>{busy ? 'Adding…' : 'Add matching leads'}</button>
          {msg && <span className="text-sm text-slate-600">{msg}</span>}
        </div>
      </form>

      <div className="card overflow-x-auto !p-0">
        <table className="w-full text-sm">
          <thead className="bg-canvas text-left text-[11px] uppercase tracking-wide text-slate-400">
            <tr><th className="p-3">Lead</th><th className="p-3">Status</th><th className="p-3">Emails sent</th><th className="p-3">Opened</th><th className="p-3">Next email</th><th className="p-3"></th></tr>
          </thead>
          <tbody>
            {campaign.enrollments.map((e) => {
              const st = stepState(e);
              return (
                <tr key={e.id} className="border-t border-line">
                  <td className="p-3">
                    <div className="font-semibold text-ink">{e.lead.businessName}</div>
                    <div className="text-xs text-slate-400">{e.lead.email} · {[e.lead.city, e.lead.country].filter(Boolean).join(', ')} · score {e.lead.opportunityScore}</div>
                  </td>
                  <td className="p-3"><Badge value={e.status} />{e.stoppedReason && e.status !== 'replied' && <div className="mt-1 text-xs text-slate-400">{e.stoppedReason}</div>}</td>
                  <td className="p-3">{st.sent} / {campaign.steps.length}</td>
                  <td className="p-3">{st.opened ? '✓ opened' : st.sent ? '—' : ''}{st.clicked ? ' · clicked' : ''}</td>
                  <td className="p-3 text-xs text-slate-500">{e.status === 'active' && e.nextSendAt ? new Date(e.nextSendAt).toLocaleString() : ''}</td>
                  <td className="p-3 text-right">
                    {e.status === 'active' && (
                      <button className="text-xs text-slate-400 hover:text-poor" onClick={() => void mailApi.stopEnrollment(campaign.id, e.id).then(onChanged)}>Stop</button>
                    )}
                  </td>
                </tr>
              );
            })}
            {!campaign.enrollments.length && <tr><td className="p-4 text-slate-400" colSpan={6}>No leads yet.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* ────────────────────────────── settings ────────────────────────────── */

function SettingsTab({ campaign, accounts, onSaved }: { campaign: CampaignDetail; accounts: MailAccount[]; onSaved: () => void }) {
  const [form, setForm] = useState({
    name: campaign.name,
    mailAccountId: campaign.mailAccountId,
    sendFromHour: campaign.sendFromHour,
    sendToHour: campaign.sendToHour,
    trackOpens: campaign.trackOpens,
    trackClicks: campaign.trackClicks,
    skipStrictCountries: campaign.skipStrictCountries,
  });
  const [msg, setMsg] = useState<string | null>(null);
  const set = <K extends keyof typeof form>(k: K, v: (typeof form)[K]) => setForm((f) => ({ ...f, [k]: v }));

  return (
    <div className="card max-w-2xl space-y-4">
      <div><label className="label">Name</label><input className="input" value={form.name} onChange={(e) => set('name', e.target.value)} /></div>
      <div>
        <label className="label">Send from</label>
        <select className="input" value={form.mailAccountId} onChange={(e) => set('mailAccountId', e.target.value)}>
          {accounts.map((a) => <option key={a.id} value={a.id}>{a.fromEmail}</option>)}
        </select>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div><label className="label">Send from (lead's local hour)</label><input className="input" type="number" min={0} max={23} value={form.sendFromHour} onChange={(e) => set('sendFromHour', Number(e.target.value))} /></div>
        <div><label className="label">Until</label><input className="input" type="number" min={1} max={24} value={form.sendToHour} onChange={(e) => set('sendToHour', Number(e.target.value))} /></div>
      </div>
      <p className="text-xs text-slate-400">Emails only go out on working days in the lead's own country: Monday–Friday in Europe, the US, Canada and the UAE; Sunday–Thursday in Saudi Arabia, Qatar, Kuwait, Bahrain and Oman.</p>
      <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.trackOpens} onChange={(e) => set('trackOpens', e.target.checked)} /> Track opens (tiny invisible image; Apple Mail can over-count)</label>
      <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.trackClicks} onChange={(e) => set('trackClicks', e.target.checked)} /> Track link clicks (rewrites links; can slightly hurt inbox placement)</label>
      <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.skipStrictCountries} onChange={(e) => set('skipStrictCountries', e.target.checked)} /> Skip Germany and Austria (cold B2B email needs prior consent there)</label>
      <div className="flex items-center gap-3">
        <button className="btn-primary" onClick={() => void mailApi.updateCampaign(campaign.id, form).then(() => { setMsg('Saved.'); onSaved(); }).catch((e) => setMsg(errMsg(e)))}>Save</button>
        {msg && <span className="text-sm text-slate-500">{msg}</span>}
      </div>
    </div>
  );
}

/* ─────────────────────────────── detail ─────────────────────────────── */

function CampaignView({ id, accounts, variables, onBack }: { id: string; accounts: MailAccount[]; variables: string[]; onBack: () => void }) {
  const [c, setC] = useState<CampaignDetail | null>(null);
  const [tab, setTab] = useState<'emails' | 'leads' | 'settings'>('emails');
  const [msg, setMsg] = useState<string | null>(null);
  const load = useCallback(() => void mailApi.campaign(id).then(setC).catch((e) => setMsg(errMsg(e))), [id]);
  useEffect(load, [load]);
  useEffect(() => {
    const t = window.setInterval(load, 30_000);
    return () => window.clearInterval(t);
  }, [load]);

  if (!c) return <div className="p-8 text-sm text-slate-400">{msg ?? 'Loading…'}</div>;

  const setStatus = (status: 'active' | 'paused') =>
    void mailApi.setStatus(c.id, status).then(() => { setMsg(null); load(); }).catch((e) => setMsg(errMsg(e)));

  return (
    <div className="mx-auto max-w-7xl space-y-6 px-6 py-8">
      <button className="text-sm text-slate-500 hover:text-ink" onClick={onBack}>← All campaigns</button>
      <div className="flex flex-wrap items-center gap-3">
        <h2 className="text-xl font-bold text-ink">{c.name}</h2>
        <Badge value={c.status} />
        <span className="text-sm text-slate-400">from {c.mailAccount?.fromEmail}</span>
        <div className="ml-auto flex gap-2">
          {c.status !== 'active' ? (
            <button className="btn-primary" onClick={() => setStatus('active')}>{c.status === 'draft' ? 'Start sending' : 'Resume'}</button>
          ) : (
            <button className="btn-ghost" onClick={() => setStatus('paused')}>Pause</button>
          )}
          {c.stats.emailsSent === 0 && (
            <button className="btn-ghost" onClick={() => void mailApi.deleteCampaign(c.id).then(onBack).catch((e) => setMsg(errMsg(e)))}>Delete</button>
          )}
        </div>
      </div>
      {msg && <p className="rounded-lg bg-red-50 p-3 text-sm text-poor">{msg}</p>}
      {c.status === 'active' && c.stats.active > 0 && (
        <p className="text-sm text-slate-500">Sending automatically: one email every 4–9 minutes, within the daily limit, during each lead's working hours.</p>
      )}
      <Funnel s={c.stats} />

      <nav className="flex gap-1 border-b border-line">
        {(['emails', 'leads', 'settings'] as const).map((t) => (
          <button key={t} onClick={() => setTab(t)} className={`-mb-px border-b-2 px-4 py-2 text-sm font-semibold capitalize ${tab === t ? 'border-brand text-ink' : 'border-transparent text-slate-500 hover:text-ink'}`}>
            {t === 'leads' ? `Leads (${c.stats.enrolled})` : t}
          </button>
        ))}
      </nav>

      {tab === 'emails' && <StepsEditor key={c.steps.map((s) => s.id).join()} campaign={c} variables={variables} onSaved={load} />}
      {tab === 'leads' && <LeadsTab campaign={c} onChanged={load} />}
      {tab === 'settings' && <SettingsTab campaign={c} accounts={accounts} onSaved={load} />}
    </div>
  );
}

/* ──────────────────────────────── page ──────────────────────────────── */

export function CampaignsPage() {
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [accounts, setAccounts] = useState<MailAccount[]>([]);
  const [presets, setPresets] = useState<Preset[]>([]);
  const [variables, setVariables] = useState<string[]>([]);
  const [open, setOpen] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);

  const load = useCallback(() => {
    void mailApi.campaigns().then(setCampaigns).catch(() => undefined);
    void mailApi.accounts().then(setAccounts).catch(() => undefined);
  }, []);
  useEffect(() => {
    load();
    void mailApi.presets().then((p) => { setPresets(p.presets); setVariables(p.variables); }).catch(() => undefined);
  }, [load]);

  if (open) return <CampaignView id={open} accounts={accounts} variables={variables} onBack={() => { setOpen(null); load(); }} />;

  return (
    <div className="mx-auto max-w-7xl space-y-6 px-6 py-8">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-bold text-ink">Campaigns</h2>
          <p className="text-sm text-slate-500">Automatic email sequences with follow-ups. A reply stops that lead's sequence at once.</p>
        </div>
        {!creating && <button className="btn-primary" onClick={() => setCreating(true)}>New campaign</button>}
      </div>

      {creating && presets.length > 0 && (
        <NewCampaign presets={presets} accounts={accounts} onCreated={(id) => { setCreating(false); setOpen(id); }} />
      )}

      <div className="grid gap-4">
        {campaigns.map((c) => (
          <button key={c.id} className="card text-left transition hover:border-brand" onClick={() => setOpen(c.id)}>
            <div className="mb-3 flex flex-wrap items-center gap-3">
              <span className="font-bold text-ink">{c.name}</span>
              <Badge value={c.status} />
              <span className="text-xs text-slate-400">{c.stepCount} emails · {c.mailAccount?.fromEmail}</span>
            </div>
            {c.stats && <Funnel s={c.stats} />}
          </button>
        ))}
        {!campaigns.length && !creating && <div className="card text-sm text-slate-500">No campaigns yet.</div>}
      </div>
    </div>
  );
}
